import { EXPIRY_PERIOD_24HOURS, EXPIRY_PERIOD_5MINUTES } from './consts.js';
import {
  getItemFromLocalStorage,
  isLocalStorageSupported,
  removeItemFromLocalStorage,
  saveItemToLocalStorage,
} from '../../../../../public-src/core/js/local-storage.js';

export interface SubscriberLS {
  newsletterPublication: string;
  newsletterId: string;
  userId: number;
  expiry: number;
}

export type NonSubscriberLS = Pick<SubscriberLS, 'newsletterId' | 'expiry'>;

export type LoginLS = Pick<SubscriberLS, 'expiry'>;

interface LocalStorageProps {
  newsletterId: string;
  newsletterPublication: string;
  userId?: number;
}

export default function handleLocalStorage({
  newsletterId,
  newsletterPublication,
  userId,
}: LocalStorageProps) {
  const setSubscribedData = () => {
    if (!isLocalStorageSupported || !userId) {
      return;
    }

    const data: SubscriberLS = {
      newsletterPublication,
      newsletterId,
      userId,
      expiry: new Date().getTime() + EXPIRY_PERIOD_24HOURS,
    };

    saveItemToLocalStorage(
      `newsletterSignup-${data.newsletterPublication}-${userId}-${newsletterId}`,
      JSON.stringify(data),
    );
  };

  const getSubscribedData = (): SubscriberLS | null => {
    if (!isLocalStorageSupported || !userId) {
      return null;
    }

    return JSON.parse(
      getItemFromLocalStorage(
        `newsletterSignup-${newsletterPublication}-${userId}-${newsletterId}`,
      ) ?? 'null',
    ) as SubscriberLS | null;
  };

  const deleteSubscribedData = () => {
    if (!isLocalStorageSupported || !userId) {
      return;
    }

    removeItemFromLocalStorage(
      `newsletterSignup-${newsletterPublication}-${userId}-${newsletterId}`,
    );
  };

  const isExpired = (expirationTime: number) => {
    const now = new Date();
    if (now.getTime() > expirationTime) {
      return true;
    }

    return false;
  };

  const userHasValidLocalStorage = (
    localStorageData: SubscriberLS | NonSubscriberLS | LoginLS | null,
  ) => {
    if (localStorageData === null) {
      return false;
    }
    if (!isExpired(localStorageData.expiry)) {
      return true;
    }

    return false;
  };

  const getNotSubscribedData = (): NonSubscriberLS | null => {
    if (!isLocalStorageSupported || !userId) {
      return null;
    }

    return JSON.parse(
      getItemFromLocalStorage(
        `newsletterSignup-notSubscriber-${newsletterPublication}-${newsletterId}-${userId}`,
      ) ?? 'null',
    ) as NonSubscriberLS | null;
  };

  const setNotSubscribedData = () => {
    if (!isLocalStorageSupported || !userId) {
      return;
    }
    const now = new Date();

    const nonSubscriber: NonSubscriberLS = {
      newsletterId,
      expiry: now.getTime() + EXPIRY_PERIOD_24HOURS,
    };

    saveItemToLocalStorage(
      `newsletterSignup-notSubscriber-${newsletterPublication}-${newsletterId}-${userId}`,
      JSON.stringify(nonSubscriber),
    );
  };

  const deleteNotSubscribedData = () => {
    if (!isLocalStorageSupported || !userId) {
      return;
    }

    removeItemFromLocalStorage(
      `newsletterSignup-notSubscriber-${newsletterPublication}-${newsletterId}-${userId}`,
    );
  };

  const getLoginData = (): LoginLS | null => {
    if (!isLocalStorageSupported) {
      return null;
    }

    return JSON.parse(
      getItemFromLocalStorage(
        `newsletterSignup-login-${newsletterPublication}-${newsletterId}`,
      ) ?? 'null',
    ) as LoginLS | null;
  };

  const setLoginData = () => {
    if (!isLocalStorageSupported) {
      return;
    }

    const data: LoginLS = {
      expiry: new Date().getTime() + EXPIRY_PERIOD_5MINUTES,
    };

    saveItemToLocalStorage(
      `newsletterSignup-login-${newsletterPublication}-${newsletterId}`,
      JSON.stringify(data),
    );
  };

  const deleteLoginData = () => {
    if (!isLocalStorageSupported) {
      return;
    }

    removeItemFromLocalStorage(
      `newsletterSignup-login-${newsletterPublication}-${newsletterId}`,
    );
  };

  const userJustLoggedInViaNewsletterWidget = (): boolean =>
    userHasValidLocalStorage(getLoginData());

  return {
    userHasValidLocalStorage,
    getSubscribedData,
    setSubscribedData,
    deleteSubscribedData,
    getNotSubscribedData,
    setNotSubscribedData,
    deleteNotSubscribedData,
    setLoginData,
    getLoginData,
    deleteLoginData,
    userJustLoggedInViaNewsletterWidget,
  };
}
